import { useEffect, useId, useRef, useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useSubmit } from '../../hooks/useSubmit';
import { refundOrder } from '../adminApi';
import { Field } from '../ui/Field';
import { AutoTextarea } from '../ui/AutoTextarea';
import { btnIcon, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { Spinner } from '../../ui/Spinner';
import { useRequestId } from '../../hooks/useRequestId';
import { paymentMethodLabel, refundProblem, refundableCents } from '../../core/rules/revenue';
import { afterRefundFailure, linesCents, refundAttempt, refundedQty } from './orderLabels';
import { ApiError } from '../../core/api/http';
import { money } from '../../core/format/currency';
import type { AdminOrder, RefundLine } from '../../core/types/adminRequests';
type Props = {
  order: AdminOrder;
  onDone: (order: AdminOrder) => void;
  onFailed: () => void;
  onClose: () => void;
};

/**
 * Terugbetalen: alles wat nog openstaat, of per regel. Eerst invullen, dan één keer bevestigen.
 * Eén `requestId` per poging; zolang bedrag en regels gelijk blijven, gaat een herhaling (na een
 * verloren antwoord) met dezelfde id, zodat Mollie nooit twee keer uitbetaalt.
 */
export function RefundForm({
  order,
  onDone,
  onFailed,
  onClose
}: Props) {
  const reasonId = useId();
  const errorId = useId();
  const left = refundableCents(order);
  const already = refundedQty(order);
  const [mode, setMode] = useState<'all' | 'lines'>('all');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // De code van de laatste fout, direct na de await te lezen (state is dan nog niet bijgewerkt).
  const latestCode = useRef<string | undefined>(undefined);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(async (...args: Parameters<typeof refundOrder>) => {
    latestCode.current = undefined;
    try {
      return await refundOrder(...args);
    } catch (err) {
      latestCode.current = err instanceof ApiError ? err.code : undefined;
      throw err;
    }
  });
  const lines: RefundLine[] = order.lines.filter(l => (qty[l.productId] ?? 0) > 0).map(l => ({
    productId: l.productId,
    qty: qty[l.productId]
  }));
  const amountCents = mode === 'all' ? left : Math.min(left, linesCents(order, lines));
  const problem = refundProblem(order, amountCents, reason);

  // Zelfde inhoud = zelfde poging. Verandert bedrag of regels, dan een nieuwe id.
  const requestId = useRequestId(refundAttempt(mode, amountCents, lines, restock));
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  const send = async () => {
    const result = await submit(order.id, {
      requestId,
      amountCents,
      reason: reason.trim(),
      ...(mode === 'lines' && lines.length ? {
        lines
      } : {}),
      ...(restock ? {
        restock: true
      } : {})
    });
    if (result) return onDone(result);
    const next = afterRefundFailure(latestCode.current);
    if (next.keepConfirming) return;
    setConfirming(false);
    if (next.reload) onFailed();
    // Terug in het formulier: focus op de melding (of het redenveld), niet verloren op <body>.
    requestAnimationFrame(() => (document.getElementById(`${errorId}-form`) ?? document.getElementById(reasonId))?.focus());
  };
  const reasonError = fieldErrors.reason;
  const formError = error && !reasonError ? error : null;
  return <div className="rounded-lg border border-line bg-shell p-4 sm:p-5" data-id="element-3546" data-mp="2jiy1jsghadn9">
      <h3 className="text-[15px] font-medium text-ink" data-id="element-3547" data-mp="2eqel8nqep4er">Terugbetalen</h3>
      <p className="mt-0.5 text-[13px] text-muted" data-id="element-3548" data-mp="2eok4llm63ygh">
        Nog terug te betalen: <span className="tabular-nums text-ink" data-id="element-3549" data-mp="2ap2vmi7j8j6k">{money(left)}</span>. Gaat via Mollie terug naar {paymentMethodLabel[order.paymentMethod]}.
      </p>

      {!confirming ? <div className="mt-4 space-y-4" data-id="element-3550" data-mp="32fpg5ol2s4kz">
          <fieldset data-id="element-3551" data-mp="32eo4pj05dg2q">
            <legend className="text-[13px] font-medium text-ink" data-id="element-3552" data-mp="2myr5ery83lh5">Wat betaal je terug?</legend>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-[14px]" data-id="element-3553" data-mp="2rxau3047bbd7">
              <label className="flex items-center gap-2" data-id="element-3554" data-mp="2rv7txn9c41ur">
                <input type="radio" name="refund-mode" checked={mode === 'all'} onChange={() => setMode('all')} className="h-4 w-4 accent-clay" data-id="element-3555" data-mp="2dz4fb2ay223e" />
                Alles ({money(left)})
              </label>
              <label className="flex items-center gap-2" data-id="element-3556" data-mp="35qa6uuclmrh2">
                <input type="radio" name="refund-mode" checked={mode === 'lines'} onChange={() => setMode('lines')} className="h-4 w-4 accent-clay" data-id="element-3557" data-mp="3ansjauptrott" />
                Per product
              </label>
            </div>
          </fieldset>

          {mode === 'lines' && <ul className="divide-y divide-line border-y border-line" data-id="element-3558" data-mp="30vw8cj2n4486">
              {order.lines.map(l => {
          const max = Math.max(0, l.qty - (already[l.productId] ?? 0));
          const n = qty[l.productId] ?? 0;
          const set = (v: number) => setQty(q => ({
            ...q,
            [l.productId]: Math.min(max, Math.max(0, v))
          }));
          return <li key={l.productId} className="flex items-center gap-3 px-3 py-2.5 text-[14px]" data-id="element-3559" data-mp="1d07x3egtagyh">
                    <span className="min-w-0 flex-1" data-id="element-3560" data-mp="1rom7qbkgz9pl">
                      <span className="block truncate text-ink" data-id="element-3561" data-mp="1mq174b1b2o0m">{l.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-3562" data-mp="z0gnrr18poqt">
                        {money(l.unitPriceCents)} per stuk{max < l.qty && ` · ${l.qty - max} al terug`}
                      </span>
                    </span>
                    <span className="flex items-center gap-1" role="group" aria-label={`Aantal ${l.name}`} data-id="element-3563" data-mp="18zxr41t0rqjm">
                      <button type="button" onClick={() => set(n - 1)} disabled={n === 0} className={btnIcon} aria-label={`Eén minder ${l.name}`} data-id="element-3564" data-mp="13z0ahtnnr4k8">
                        <MinusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3565" data-mp="2feypsf9fimtw" />
                      </button>
                      <span className="w-10 text-center tabular-nums" aria-live="polite" data-id="element-3566" data-mp="2miofzrwiyb2k">
                        {n} / {max}
                      </span>
                      <button type="button" onClick={() => set(n + 1)} disabled={n >= max} className={btnIcon} aria-label={`Eén meer ${l.name}`} data-id="element-3567" data-mp="2rjchj7idqab1">
                        <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3568" data-mp="31fp8vyqgcc2m" />
                      </button>
                    </span>
                  </li>;
        })}
            </ul>}

          <label className="flex items-start gap-2.5 text-[14px] text-ink" data-id="element-3569" data-mp="28r9unef59xsc">
            <input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-3570" data-mp="2dotlrewxy51p" />
            <span data-id="element-3571" data-mp="2iio0s51lpvug">
              Terug in de webshopvoorraad
              <span className="block text-[12px] text-muted" data-id="element-3572" data-mp="1l04j3bg8lrnu">Alleen als het ongeopend terug is. Geopend of niet teruggestuurd: uit laten.</span>
            </span>
          </label>
          {order.voucherCents > 0 && <p className="text-[12px] text-muted" data-id="element-3573" data-mp="74mdfnpkj436">
              Deels betaald met een cadeaubon ({money(order.voucherCents)}): dat deel komt naar verhouding terug op de bon.
            </p>}

          <Field label="Reden" hint="Staat bij de terugbetaling en in de boekhouding. De klant ziet hem niet." data-id="element-3574" data-mp="3u80upptay2oz">
            <AutoTextarea id={reasonId} value={reason} onChange={e => setReason(e.target.value)} minRows={2} maxLength={300} className={inputCls} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? errorId : undefined} data-id="element-3575" data-mp="3pcv96s3taq1f" />
          </Field>
          {reasonError && <p id={errorId} role="alert" className="-mt-2 text-[12px] text-clay" data-id="element-3576" data-mp="31l6mbr9retrv">
              {reasonError}
            </p>}
          {formError && <p id={`${errorId}-form`} tabIndex={-1} role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink outline-none" data-id="element-3577" data-mp="2wmve1kz44rbr">
              {formError}
            </p>}

          <div className="flex flex-wrap gap-2" data-id="element-3578" data-mp="tbp2vuucyr50">
            <button type="button" onClick={() => setConfirming(true)} disabled={problem !== null} className={btnPrimary} data-id="element-3579" data-mp="fd8l98uyjg83">
              {money(amountCents)} terugbetalen…
            </button>
            <button type="button" onClick={onClose} className={btnSecondary} data-id="element-3580" data-mp="5n5ulfbb6ecx">
              Niet terugbetalen
            </button>
          </div>
          {mode === 'lines' && amountCents === 0 ? <p className="text-[12px] text-muted" data-id="element-3581" data-mp="3srvehdwr4eo9">Kies minstens één product.</p> : problem?.field === 'reason' && <p className="text-[12px] text-muted" data-id="element-3582" data-mp="1mdkkjum45fl">Vul eerst een reden in om verder te gaan.</p>}
        </div> : <div className="mt-4 rounded-md bg-sand px-3 py-3 text-[14px]" role="alertdialog" aria-label="Terugbetaling bevestigen" aria-describedby={`${errorId}-confirm`} data-id="element-3583" data-mp="3iu7iylrbn351">
          <p id={`${errorId}-confirm`} className="text-ink" data-id="element-3584" data-mp="347s6bymtih04">
            <strong className="font-medium" data-id="element-3585" data-mp="305exv2gos4zn">{money(amountCents)}</strong> terugbetalen aan {order.customerName || order.email}? Dit kan niet ongedaan worden
            gemaakt. De klant krijgt een mail.
          </p>
          {error && <p role="alert" className="mt-2 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3586" data-mp="39521zw0svowe">
              {error}
            </p>}
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-3587" data-mp="3j3znsr6ud4bp">
            <button ref={confirmRef} type="button" onClick={() => void send()} disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-3588" data-mp="3j37ytz7r24ue">
              {submitting && <Spinner data-id="element-3589" data-mp="3rvt88ophqz6k" />}
              {submitting ? 'Bezig met terugbetalen…' : 'Ja, terugbetalen'}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={submitting} className={btnSecondary} data-id="element-3590" data-mp="ap96r8nwf3m6">
              Terug
            </button>
          </div>
        </div>}
    </div>;
}